import React, { useMemo } from 'react';
import { Stack, Banner, Card, AnchorTextLink } from '@chaser/components';
import useOrganisation from '../../../../hooks/useOrganisation';
import { useDispatch } from 'react-redux';
import useTipDismissed from '../../../../hooks/useTipDismissed';
import NavigationTabs from '../../../../components/NavigationTabs';
import { TabPanel, TabPanels } from '@chaser/components';
import ImportService from '../../../../services/import-service';
import useFetch from '../../../../hooks/useFetch';
import GeneralConfiguration from '../GeneralConfiguration';
import Mapping from '../Mapping';
import { SupportedDateFormatsContext } from './supportedDateFormatsContext';
import TickIcon from '../../../../assets/icon-tick.svg';

const Configure = () => {
  const dispatch = useDispatch();
  const tipDismissed = useTipDismissed('mapping-configuration-tip');
  const { ShortCode } = useOrganisation();
  const { data: csvMapping, refetch: refetchCSVMapping } = useFetch(
    ImportService.getCSVMapping,
    ShortCode,
  );
  const { data: csvMappingSpecification } = useFetch(
    ImportService.getCSVMappingSpecification,
    ShortCode,
  );

  const tabs = useMemo(() => {
    if (!csvMappingSpecification || !ShortCode) {
      return [];
    }

    return [
      {
        path: 'general',
        title: 'General',
        render: () => (
          <GeneralConfiguration
            shortCode={ShortCode}
            configuration={csvMapping?.configuration}
            onCompleted={() => refetchCSVMapping()}
          />
        ),
      },
      ...(csvMappingSpecification.types.map(type => {
        const mapping = csvMapping?.types?.find(t => t.id === type.id);
        const isCompleted = Boolean(mapping);

        return {
          path: type.id,
          title: type.name,
          label: isCompleted ? (
            <img src={TickIcon} alt="" aria-label="Completed" width={14} height={10} />
          ) : null,
          render: () => (
            <Mapping
              type={type}
              shortCode={ShortCode}
              maxFileSize={csvMappingSpecification.maxCsvSize}
              mapping={mapping}
              onCompleted={() => refetchCSVMapping()}
            />
          ),
        };
      }) || []),
    ];
  }, [ShortCode, csvMappingSpecification, csvMapping, refetchCSVMapping]);

  async function handleClose() {
    dispatch({
      type: 'DISMISS_TIP_ALERT',
      name: 'mapping-configuration-tip',
      shortCode: ShortCode,
    });
  }

  return (
    <SupportedDateFormatsContext.Provider value={csvMappingSpecification?.supportedDateFormats}>
      <Stack gap="small">
        {!tipDismissed && (
          <Banner
            onClose={handleClose}
            backgroundColor="Light Neutral 2"
            borderColor="transparent"
            icon="alert-square"
          >
            Mapping is optional. Alternatively you can upload your data in the{' '}
            <AnchorTextLink href="https://storage.googleapis.com/chaser-staticfiles/PRODUCT - 202403 - Updated CSV Master document.xlsx">
              accepted format
            </AnchorTextLink>
            .
          </Banner>
        )}
        <Card p="medium">
          <NavigationTabs basePath={'/import/configure'} tabs={tabs}>
            <TabPanels>
              {tabs.map(({ path, render }) => (
                <TabPanel key={path}>{render()}</TabPanel>
              ))}
            </TabPanels>
          </NavigationTabs>
        </Card>
      </Stack>
    </SupportedDateFormatsContext.Provider>
  );
};

Configure.propTypes = {};

export default Configure;
